import { useState } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { C, F, R, T, S, NAV_H, PATH_H, SIDE_W, SIDE_W_COLLAPSED } from "../../../../../CRM/frontend/src/design/tokens";
import { formatIsoWeek } from "../util/isoWeek";

// Three-region shell modeled on CRM/frontend/src/layouts/BaseLayout.js
// (dark nav + breadcrumb strip + collapsible sidebar + routed content).
// Deliberately not importing BaseLayout itself — it depends on CRM-only
// context providers (ComposeProvider, SidebarContext) that don't apply here.

const SIDEBAR_TABS = [
  { id: "overview",         label: "Overview",         path: "/overview" },
  { id: "domains",          label: "Domains",          path: "/domains" },
  { id: "iso-week",         label: "ISO Week",         path: "/iso-week" },
  { id: "timeline",         label: "Timeline",         path: "/timeline" },
  { id: "content-pipeline", label: "Content Pipeline", path: "/content-pipeline" },
  { id: "analytics",        label: "Analytics",        path: "/analytics" },
];

const CRUMBS = SIDEBAR_TABS.reduce((acc, tab) => {
  acc[tab.path] = tab.label;
  return acc;
}, {});

export default function XtmLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const sidebarW = sidebarCollapsed ? SIDE_W_COLLAPSED : SIDE_W;
  const activeTabId = SIDEBAR_TABS.find(t => location.pathname.startsWith(t.path))?.id || null;

  const crumbs = [];
  for (const [prefix, label] of Object.entries(CRUMBS)) {
    if (location.pathname.startsWith(prefix)) crumbs.push({ label, path: prefix });
  }

  const handleLogout = async () => {
    localStorage.removeItem("xtm_token");
    try {
      await instance.clearCache();
    } catch {
      Object.keys(sessionStorage)
        .filter(k => k.includes("msal") || k.includes("login.windows"))
        .forEach(k => sessionStorage.removeItem(k));
    }
    navigate("/sign-in");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", fontFamily: F.family, background: C.surfaceBase }}>

      {/* ── Nav bar ── */}
      <div style={{
        height: NAV_H, background: C.nav, borderBottom: `1px solid ${C.navBorder}`,
        display: "flex", alignItems: "center", padding: "0 16px", gap: 16, flexShrink: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => navigate("/overview")}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <polygon points="9,2 17,16 1,16" stroke="white" strokeWidth="1.4" fill="none" />
          </svg>
          <span style={{ color: C.white, fontSize: F.lg, fontWeight: F.semibold }}>XTM</span>
        </div>
        <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.15)" }} />
        <span style={{ color: C.grey30, fontSize: F.base }}>ACG Health Solutions</span>

        {/* Static ISO week label — confirms the isocalendar computation works */}
        <div style={{
          marginLeft: "auto", display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{
            fontSize: F.sm, color: C.grey30, padding: "4px 10px",
            border: "1px solid rgba(255,255,255,0.15)", borderRadius: R.pill,
            letterSpacing: "0.02em",
          }}>
            {formatIsoWeek()}
          </span>
          <button onClick={handleLogout} style={{
            width: 32, height: 32, borderRadius: "50%", background: C.primary, border: "none",
            color: C.white, fontSize: F.sm, fontWeight: F.semibold, cursor: "pointer",
          }} title="Sign out">
            X
          </button>
        </div>
      </div>

      {/* ── Breadcrumb bar ── */}
      <div style={{
        height: PATH_H, background: C.surfaceCard, borderBottom: `1px solid ${C.borderLight}`,
        display: "flex", alignItems: "center", padding: "0 24px", fontSize: F.sm, color: C.textSecondary, flexShrink: 0,
      }}>
        <Link to="/overview" style={{ fontSize: F.sm, color: C.textSecondary, textDecoration: "none" }}>Home</Link>
        {crumbs.map((crumb, i) => {
          const isCurrent = i === crumbs.length - 1;
          return (
            <span key={crumb.path} style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 6 }}>
              <span style={{ color: C.textDisabled }}>{"›"}</span>
              <Link to={crumb.path} style={{
                fontSize: F.sm, color: isCurrent ? C.textPrimary : C.textSecondary,
                fontWeight: isCurrent ? F.medium : F.regular, textDecoration: "none",
              }}>
                {crumb.label}
              </Link>
            </span>
          );
        })}
      </div>

      {/* ── Page content ── */}
      <div style={{ flex: 1, overflow: "hidden", background: C.surfaceBase, display: "flex" }}>
        <div style={{
          width: sidebarW, flexShrink: 0, background: C.sidebar, borderRight: `1px solid ${C.navBorder}`,
          transition: T.expand, overflowX: "hidden", overflowY: "auto", paddingTop: S.sm,
          display: "flex", flexDirection: "column",
        }}>
          <div
            onClick={() => setSidebarCollapsed(c => !c)}
            style={{ height: 28, margin: `2px ${S.sm}px 6px`, display: "flex", alignItems: "center",
              justifyContent: sidebarCollapsed ? "center" : "flex-end", cursor: "pointer", color: C.sidebarText }}
            title={sidebarCollapsed ? "Expand" : "Collapse"}
          >
            {sidebarCollapsed ? "»" : "«"}
          </div>
          {SIDEBAR_TABS.map(tab => {
            const isActive = activeTabId === tab.id;
            return (
              <div key={tab.id}
                onClick={() => navigate(tab.path)}
                title={sidebarCollapsed ? tab.label : undefined}
                style={{
                  height: 36, display: "flex", alignItems: "center", gap: 12,
                  padding: sidebarCollapsed ? "0" : "0 12px", margin: `2px ${S.sm}px`,
                  borderRadius: R.md, cursor: "pointer", fontSize: F.base,
                  color: isActive ? C.primary : C.sidebarText,
                  fontWeight: isActive ? F.semibold : F.regular,
                  background: isActive ? C.sidebarActiveBg : "transparent",
                  transition: T.base, justifyContent: sidebarCollapsed ? "center" : "flex-start",
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = C.sidebarHover; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
              >
                {!sidebarCollapsed && <span>{tab.label}</span>}
                {sidebarCollapsed && <span>{tab.label[0]}</span>}
              </div>
            );
          })}
        </div>

        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export { SIDEBAR_TABS };
